import { createFlagsmithInstance } from 'flagsmith'
import { defineStore } from 'pinia'
import { reactive, ref, watch } from 'vue'

import { useEnvironmentStore } from '@/stores/environment'

import { devtools } from '@/plugins/devtools/api'
import { hotReloadStore } from '@/utils/build'

export type FlagOptions =
  | 'flag-one' // for testing
  | 'attachment-mergeable'
  | 'permanent-announcement-banner'
  | 'document-history'
  | 'pfcapability-enabled'
export type TraitOptions = ''

interface FlagDetails<T> {
  /**
   * Whether the flag is enabled.
   */
  enabled: boolean
  /**
   * The value of the flag.
   * This will always return the value even if the flag is disabled.
   */
  value: T | null
  /**
   * The timestamp when the flag was last updated.
   * This is mainly used to trigger reactivity when the flag is updated.
   */
  updatedAt: number
}

export interface FlagStore {
  /**
   * Gets details about a flag from the Flagsmith instance.
   * This is reactive and will update when the flag is updated.
   *
   * The first call initializes Flagsmith, so flags are disabled until it has loaded.
   */
  getFlag: <T>(flag: FlagOptions) => FlagDetails<T>
}

/**
 * How often to poll for flags and how long the cache is valid.
 *
 * Set to 30 seconds.
 */
const REFRESH_INTERVAL = 30 * 1000

/**
 * A unique value to represent a missing flag.
 * Used to alert when flags are missing, then replaced by default `false`.
 */
const FALLBACK_VALUE = Symbol('fallback-value')

/**
 * Controls feature flags using Flagsmith.
 */
export const useFlagStore = defineStore('flag', (): FlagStore => {
  const flagsmith = createFlagsmithInstance<FlagOptions, TraitOptions>()

  /**
   * A record to keep track of when flags were last updated.
   * This is mainly used to trigger reactivity when a flag is updated.
   */
  const flagAges: Record<string, number> = reactive({})

  const environmentStore = useEnvironmentStore()
  const initialized = ref(false)
  async function init (): Promise<void> {
    await environmentStore.loadEnvironment()
    const flagsmithDetails = environmentStore.flagsmithDetails
    if (!flagsmithDetails) {
      console.warn('Flagsmith details are not available. Skipping Flagsmith initialization.')
      return
    }
    // https://docs.flagsmith.com/clients/javascript#initialisation-options
    await flagsmith.init({
      api: flagsmithDetails.url,
      environmentID: flagsmithDetails.key,
      cacheFlags: true,
      cacheOptions: {
        ttl: REFRESH_INTERVAL,
        skipAPI: true,
        loadStale: true,
      },
      enableAnalytics: false,
      onChange (_oldFlags, params) {
        if (!params.flagsChanged) return
        const now = Date.now()
        for (const flag of params.flagsChanged) {
          flagAges[flag] = now
        }
      },
    })

    flagsmith.startListening(REFRESH_INTERVAL)
    initialized.value = true
  }
  let initStarted = false
  function ensureInitialized (): void {
    if (initStarted) return
    initStarted = true
    void init()
  }

  function getMessageId (flag: string): string {
    return `flag--${flag}`
  }
  const flagStatuses = reactive(new Map<string, 'valid' | 'missing' | 'warned'>())
  watch([flagStatuses, initialized], ([statuses, ini]) => {
    if (!ini) return
    for (const [flag, status] of statuses.entries()) {
      if (status !== 'missing') continue
      devtools.addMessage({
        id: getMessageId(flag),
        text: `Missing flag: ${flag}`,
        color: 'error',
      })
      statuses.set(flag, 'warned')
    }
  })

  function getFeature (flag: FlagOptions): boolean {
    const hasFeature = flagsmith.hasFeature(flag, { fallback: FALLBACK_VALUE as unknown as boolean }) as boolean | symbol
    switch (flagStatuses.get(flag)) {
      case 'warned':
        if (hasFeature !== FALLBACK_VALUE) {
          const id = getMessageId(flag)
          void devtools.removeMessage(id, {
            id: `${id}-removed`,
            text: `Flag now found: ${flag}`,
            color: 'success',
          })
          flagStatuses.set(flag, 'valid')
        }
        break
      default:
        flagStatuses.set(flag, hasFeature === FALLBACK_VALUE ? 'missing' : 'valid')
    }
    return hasFeature === true
  }
  const getFlag: FlagStore['getFlag'] = <T>(flag: FlagOptions) => {
    ensureInitialized()
    return {
      enabled: getFeature(flag),
      value: flagsmith.getValue(flag, { fallback: null }) as T | null,
      updatedAt: flagAges[flag] || 0,
    }
  }

  return {
    getFlag,
  }
})

hotReloadStore(useFlagStore)
